<script setup>
import { ref, computed } from 'vue';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Head, Link } from '@inertiajs/inertia-vue3';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import { Icon } from '@iconify/vue';
import axios from 'axios';

const props = defineProps({
    roles: Array,
    rights: Array,
    families: Object,
});

// Lista local reactiva de roles
const localRoles = ref([...(props.roles || [])]);

// Rol activo seleccionado
const selectedRoleId = ref(localRoles.value.length > 0 ? localRoles.value[0].id : null);
const selectedRole = computed(() => {
    return localRoles.value.find(r => r.id === selectedRoleId.value) || localRoles.value[0];
});

// Mapa de permisos activos por rol en el estado local del componente
const roleRightsMap = ref({});
localRoles.value.forEach(role => {
    roleRightsMap.value[role.id] = [...(role.rights || [])];
});

// Permisos seleccionados para el rol actual
const currentRights = computed({
    get: () => roleRightsMap.value[selectedRoleId.value] || [],
    set: (val) => {
        roleRightsMap.value[selectedRoleId.value] = val;
    }
});

// Agrupación de rights por familia
const groupedRights = computed(() => {
    const familyOrder = ['menu', 'permisos', 'agenda', 'comunicados', 'cursos', 'simulacro', 'financial', 'rrhh'];
    const groups = {};
    familyOrder.forEach(f => {
        groups[f] = [];
    });

    props.rights.forEach(right => {
        const family = right.family || 'general';
        if (!groups[family]) {
            groups[family] = [];
        }
        groups[family].push(right);
    });

    const orderedResult = {};
    Object.keys(groups).forEach(f => {
        if (groups[f].length > 0) {
            orderedResult[f] = groups[f];
        }
    });

    return orderedResult;
});

// Estado de guardado y mensajes
const isSaving = ref(false);
const saveMessage = ref('');
const saveMessageType = ref('success');

// Modal Crear Rol
const showCreateModal = ref(false);
const isCreatingRole = ref(false);
const createForm = ref({
    label: '',
    description: '',
    status: 1,
    cloneFromRoleId: null
});
const createErrors = ref({});

// Modal Editar Rol
const showEditModal = ref(false);
const isUpdatingRole = ref(false);
const editForm = ref({
    id: null,
    label: '',
    description: '',
    status: 1
});
const editErrors = ref({});

const openCreateModal = () => {
    createForm.value = {
        label: '',
        description: '',
        status: 1,
        cloneFromRoleId: null
    };
    createErrors.value = {};
    showCreateModal.value = true;
};

const openEditModal = (role) => {
    if (role.is_reserved) return;
    editForm.value = {
        id: role.id,
        label: role.label,
        description: role.description || '',
        status: role.status ?? 1
    };
    editErrors.value = {};
    showEditModal.value = true;
};

const submitCreateRole = async () => {
    createErrors.value = {};
    if (!createForm.value.label.trim()) {
        createErrors.value.label = 'El nombre del rol es obligatorio.';
        return;
    }

    isCreatingRole.value = true;
    try {
        let initialRights = [];
        if (createForm.value.cloneFromRoleId) {
            initialRights = [...(roleRightsMap.value[createForm.value.cloneFromRoleId] || [])];
        }

        const response = await axios.post(route('roles.store'), {
            label: createForm.value.label.trim(),
            description: createForm.value.description ? createForm.value.description.trim() : null,
            status: createForm.value.status,
            rights: initialRights
        });

        if (response.data.success && response.data.role) {
            const newRole = response.data.role;
            localRoles.value.push(newRole);
            roleRightsMap.value[newRole.id] = [...(newRole.rights || [])];
            selectedRoleId.value = newRole.id;

            showCreateModal.value = false;
            saveMessageType.value = 'success';
            saveMessage.value = response.data.message || `Rol "${newRole.label}" creado exitosamente. Ahora puede personalizar sus permisos.`;
        }
    } catch (error) {
        if (error.response?.status === 422 && error.response?.data?.errors) {
            createErrors.value = error.response.data.errors;
        } else {
            saveMessageType.value = 'error';
            saveMessage.value = error.response?.data?.message || 'Error al crear el rol.';
        }
    } finally {
        isCreatingRole.value = false;
        setTimeout(() => {
            if (saveMessageType.value === 'success') saveMessage.value = '';
        }, 5000);
    }
};

const submitUpdateRole = async () => {
    editErrors.value = {};
    if (!editForm.value.label.trim()) {
        editErrors.value.label = 'El nombre del rol es obligatorio.';
        return;
    }

    isUpdatingRole.value = true;
    try {
        const response = await axios.put(route('roles.update', editForm.value.id), {
            label: editForm.value.label.trim(),
            description: editForm.value.description ? editForm.value.description.trim() : null,
            status: editForm.value.status
        });

        if (response.data.success && response.data.role) {
            const updated = response.data.role;
            const index = localRoles.value.findIndex(r => r.id === updated.id);
            if (index !== -1) {
                localRoles.value[index] = { ...localRoles.value[index], ...updated };
            }

            showEditModal.value = false;
            saveMessageType.value = 'success';
            saveMessage.value = response.data.message || `Rol "${updated.label}" actualizado correctamente.`;
        }
    } catch (error) {
        if (error.response?.status === 422 && error.response?.data?.errors) {
            editErrors.value = error.response.data.errors;
        } else {
            saveMessageType.value = 'error';
            saveMessage.value = error.response?.data?.message || 'Error al actualizar el rol.';
        }
    } finally {
        isUpdatingRole.value = false;
        setTimeout(() => {
            if (saveMessageType.value === 'success') saveMessage.value = '';
        }, 4000);
    }
};

const toggleRight = (rightId) => {
    if (selectedRole.value?.is_reserved) return;
    const current = [...currentRights.value];
    const index = current.indexOf(rightId);
    if (index > -1) {
        current.splice(index, 1);
    } else {
        current.push(rightId);
    }
    roleRightsMap.value[selectedRoleId.value] = current;
};

const selectAllFamily = (familyKey) => {
    if (selectedRole.value?.is_reserved) return;
    const familyRights = groupedRights.value[familyKey] || [];
    const currentSet = new Set(currentRights.value);
    familyRights.forEach(r => currentSet.add(r.id));
    roleRightsMap.value[selectedRoleId.value] = Array.from(currentSet);
};

const deselectAllFamily = (familyKey) => {
    if (selectedRole.value?.is_reserved) return;
    const familyRights = groupedRights.value[familyKey] || [];
    const familyIds = new Set(familyRights.map(r => r.id));
    roleRightsMap.value[selectedRoleId.value] = currentRights.value.filter(id => !familyIds.has(id));
};

const selectAllAll = () => {
    if (selectedRole.value?.is_reserved) return;
    roleRightsMap.value[selectedRoleId.value] = props.rights.map(r => r.id);
};

const deselectAllAll = () => {
    if (selectedRole.value?.is_reserved) return;
    roleRightsMap.value[selectedRoleId.value] = [];
};

const saveRoleRights = async () => {
    if (selectedRole.value?.is_reserved) return;
    isSaving.value = true;
    saveMessage.value = '';

    try {
        const response = await axios.put(route('roles.rights.update', selectedRoleId.value), {
            rights: currentRights.value
        });

        if (response.data.success) {
            saveMessageType.value = 'success';
            saveMessage.value = response.data.message || 'Permisos actualizados con éxito.';
            roleRightsMap.value[selectedRoleId.value] = response.data.rights;
        } else {
            saveMessageType.value = 'error';
            saveMessage.value = response.data.message || 'Error al actualizar permisos.';
        }
    } catch (error) {
        saveMessageType.value = 'error';
        saveMessage.value = error.response?.data?.message || 'Ocurrió un error inesperado.';
    } finally {
        isSaving.value = false;
        setTimeout(() => {
            if (saveMessageType.value === 'success') saveMessage.value = '';
        }, 4000);
    }
};

const getRoleBadgeClass = (role) => {
    if (!role) return '';
    if (role.is_reserved) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200';
    if (role.id <= 3) return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 border-indigo-200';
    return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200';
};
</script>

<template>
    <Head title="Roles y Permisos de Usuarios" />

    <BreezeAuthenticatedLayout>
        <div class="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
            <!-- Header -->
            <PremiumPageHeader
                title="Gestión de Roles y Permisos"
                subtitle="Cree nuevos roles de colaboradores y configure su matriz de acceso global y derechos por módulo"
                icon="tabler:shield-lock"
            >
                <template #actions>
                    <div class="flex items-center gap-2">
                        <button
                            @click="openCreateModal"
                            class="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-md shadow-indigo-600/20 hover:shadow-indigo-600/30 cursor-pointer"
                        >
                            <Icon icon="tabler:plus" class="w-4 h-4" />
                            <span>Nuevo Rol</span>
                        </button>
                        <Link :href="route('config')" class="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors shadow-sm">
                            <Icon icon="tabler:arrow-left" class="w-4 h-4" />
                            Volver
                        </Link>
                    </div>
                </template>
            </PremiumPageHeader>

            <!-- Alerta Flotante de Notificación -->
            <div v-if="saveMessage" class="p-4 rounded-xl text-sm font-semibold flex items-center justify-between shadow-lg transition-all" :class="saveMessageType === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'">
                <div class="flex items-center gap-3">
                    <Icon :icon="saveMessageType === 'success' ? 'tabler:check' : 'tabler:alert-circle'" class="w-5 h-5" />
                    <span>{{ saveMessage }}</span>
                </div>
                <button @click="saveMessage = ''" class="opacity-80 hover:opacity-100 cursor-pointer">
                    <Icon icon="tabler:x" class="w-4 h-4" />
                </button>
            </div>

            <!-- Contenido Principal: Grid de 2 Columnas -->
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                
                <!-- Columna Izquierda: Lista de Roles (4 de 12) -->
                <div class="lg:col-span-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-4 space-y-3">
                    <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
                        <div class="flex items-center gap-2 text-slate-800 dark:text-white font-bold text-sm">
                            <Icon icon="tabler:users" class="w-4 h-4 text-indigo-500" />
                            <span>Roles del Sistema</span>
                        </div>
                        <button
                            @click="openCreateModal"
                            class="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 p-1 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-lg transition-colors cursor-pointer"
                            title="Crear nuevo rol"
                        >
                            <Icon icon="tabler:plus" class="w-3.5 h-3.5" />
                            <span>Crear</span>
                        </button>
                    </div>

                    <!-- Lista de Roles -->
                    <div class="space-y-1.5 max-h-[calc(100vh-280px)] overflow-y-auto custom-scrollbar pr-1">
                        <button
                            v-for="role in localRoles"
                            :key="role.id"
                            @click="selectedRoleId = role.id"
                            class="w-full text-left p-3 rounded-xl transition-all flex items-center justify-between gap-3 border cursor-pointer"
                            :class="selectedRoleId === role.id
                                ? 'bg-indigo-50/80 dark:bg-indigo-950/40 border-indigo-300 dark:border-indigo-800 shadow-sm text-indigo-950 dark:text-indigo-200'
                                : 'bg-transparent border-transparent hover:bg-slate-50 dark:hover:bg-slate-750/50 text-slate-700 dark:text-slate-300'"
                        >
                            <div class="min-w-0 flex-1">
                                <div class="flex items-center gap-2">
                                    <span class="w-5 h-5 rounded-md bg-slate-200 dark:bg-slate-700 text-[11px] font-bold flex items-center justify-center text-slate-700 dark:text-slate-300 shrink-0">
                                        {{ role.id }}
                                    </span>
                                    <h4 class="text-sm font-semibold truncate">{{ role.label }}</h4>
                                </div>
                                <p class="text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5 ml-7">
                                    {{ role.description || 'Sin descripción' }}
                                </p>
                            </div>

                            <div class="shrink-0 flex items-center gap-1.5">
                                <span v-if="role.is_reserved" class="text-[10px] uppercase font-bold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-900">
                                    Reservado
                                </span>
                                <span v-else class="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                    {{ (roleRightsMap[role.id] || []).length }} perm.
                                </span>
                                <Icon icon="tabler:chevron-right" class="w-4 h-4 text-slate-400" />
                            </div>
                        </button>
                    </div>
                </div>

                <!-- Columna Derecha: Matriz de Permisos del Rol Seleccionado (8 de 12) -->
                <div v-if="selectedRole" class="lg:col-span-8 space-y-6">
                    
                    <!-- Tarjeta de Información del Rol y Acciones -->
                    <div class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 space-y-4">
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100 dark:border-slate-700">
                            <div>
                                <div class="flex items-center gap-3">
                                    <h2 class="text-xl font-bold text-slate-900 dark:text-white">
                                        {{ selectedRole.label }}
                                    </h2>
                                    <span class="text-xs font-bold px-2.5 py-1 rounded-full border" :class="getRoleBadgeClass(selectedRole)">
                                        ID #{{ selectedRole.id }}
                                    </span>
                                    <button
                                        v-if="!selectedRole.is_reserved"
                                        @click="openEditModal(selectedRole)"
                                        class="p-1 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
                                        title="Editar nombre y descripción del rol"
                                    >
                                        <Icon icon="tabler:edit" class="w-4 h-4" />
                                    </button>
                                </div>
                                <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {{ selectedRole.description || 'Perfil de rol sin descripción adicional.' }}
                                </p>
                            </div>

                            <!-- Botón Guardar -->
                            <div v-if="!selectedRole.is_reserved" class="flex items-center gap-2">
                                <button
                                    @click="saveRoleRights"
                                    :disabled="isSaving"
                                    class="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-xs sm:text-sm transition-all shadow-md shadow-indigo-600/20 hover:shadow-indigo-600/30 flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                                >
                                    <Icon v-if="isSaving" icon="tabler:loader-2" class="w-4 h-4 animate-spin" />
                                    <Icon v-else icon="tabler:device-floppy" class="w-4 h-4" />
                                    <span>{{ isSaving ? 'Guardando...' : 'Guardar Permisos' }}</span>
                                </button>
                            </div>
                        </div>

                        <!-- Barra de Herramientas de Selección Rápida -->
                        <div v-if="!selectedRole.is_reserved" class="flex flex-wrap items-center justify-between gap-3 text-xs">
                            <span class="text-slate-500 dark:text-slate-400 font-medium">
                                <strong class="text-indigo-600 dark:text-indigo-400">{{ currentRights.length }}</strong> de {{ rights.length }} permisos asignados a este rol.
                            </span>
                            <div class="flex items-center gap-2">
                                <button @click="selectAllAll" class="px-2.5 py-1 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors font-medium cursor-pointer">
                                    Marcar todos
                                </button>
                                <span class="text-slate-300 dark:text-slate-600">|</span>
                                <button @click="deselectAllAll" class="px-2.5 py-1 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors font-medium cursor-pointer">
                                    Desmarcar todos
                                </button>
                            </div>
                        </div>

                        <!-- Alerta para roles reservados -->
                        <div v-if="selectedRole.is_reserved" class="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 flex items-start gap-3">
                            <Icon icon="tabler:alert-triangle" class="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                            <div class="text-xs text-amber-800 dark:text-amber-300">
                                <strong>Rol Reservado:</strong> Este rol corresponde al portal exclusivo de estudiantes o apoderados. Sus permisos son gestionados automáticamente por las políticas del sistema y no son modificables desde esta matriz.
                            </div>
                        </div>
                    </div>

                    <!-- Secciones de Permisos Agrupados por Familia -->
                    <div class="space-y-6">
                        <div
                            v-for="(familyInfo, familyKey) in families"
                            :key="familyKey"
                            class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
                        >
                            <!-- Cabecera de Familia -->
                            <div class="p-4 sm:p-5 bg-slate-50/80 dark:bg-slate-850/60 border-b border-slate-100 dark:border-slate-700/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div class="flex items-center gap-3">
                                    <div class="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/50">
                                        <Icon :icon="familyInfo.icon" class="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 class="text-sm font-bold text-slate-800 dark:text-white">
                                            {{ familyInfo.title }}
                                        </h3>
                                        <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                                            {{ familyInfo.description }}
                                        </p>
                                    </div>
                                </div>

                                <div v-if="!selectedRole.is_reserved && groupedRights[familyKey]?.length" class="flex items-center gap-1.5 self-end sm:self-center text-xs">
                                    <button
                                        @click="selectAllFamily(familyKey)"
                                        class="px-2 py-1 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-md font-semibold transition-colors cursor-pointer"
                                    >
                                        Todos
                                    </button>
                                    <span class="text-slate-300 dark:text-slate-600">·</span>
                                    <button
                                        @click="deselectAllFamily(familyKey)"
                                        class="px-2 py-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md font-semibold transition-colors cursor-pointer"
                                    >
                                        Ninguno
                                    </button>
                                </div>
                            </div>

                            <!-- Matriz de Permisos de la Familia -->
                            <div class="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div
                                    v-for="right in (groupedRights[familyKey] || [])"
                                    :key="right.id"
                                    @click="toggleRight(right.id)"
                                    class="flex items-start gap-3 p-3.5 rounded-xl border transition-all select-none"
                                    :class="[
                                        selectedRole.is_reserved ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
                                        currentRights.includes(right.id)
                                            ? 'bg-indigo-50/50 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-800/80 shadow-xs'
                                            : 'bg-white dark:bg-slate-900/60 border-slate-200/80 dark:border-slate-750 hover:bg-slate-50 dark:hover:bg-slate-850'
                                    ]"
                                >
                                    <input
                                        type="checkbox"
                                        :checked="currentRights.includes(right.id)"
                                        :disabled="selectedRole.is_reserved"
                                        class="mt-1 rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 shrink-0 pointer-events-none"
                                    />
                                    <div class="flex-1 min-w-0">
                                        <div class="flex items-center justify-between gap-2">
                                            <span class="text-xs font-bold text-slate-800 dark:text-slate-200">
                                                {{ right.label }}
                                            </span>
                                            <span class="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 shrink-0">
                                                {{ right.module }}.{{ right.perms }}
                                            </span>
                                        </div>
                                        <p class="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                                            Identificador: <span class="font-mono text-slate-500 dark:text-slate-400">#{{ right.id }}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
        </div>

        <!-- Modal Crear Nuevo Rol -->
        <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
            <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div class="p-6 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div class="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
                            <Icon icon="tabler:shield-plus" class="w-6 h-6" />
                        </div>
                        <div>
                            <h3 class="text-lg font-bold text-slate-900 dark:text-white">Crear Nuevo Rol</h3>
                            <p class="text-xs text-slate-500 dark:text-slate-400">Defina el nuevo perfil para asignar permisos específicos</p>
                        </div>
                    </div>
                    <button @click="showCreateModal = false" class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer">
                        <Icon icon="tabler:x" class="w-5 h-5" />
                    </button>
                </div>

                <form @submit.prevent="submitCreateRole" class="p-6 space-y-4">
                    <!-- Nombre del Rol -->
                    <div>
                        <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                            Nombre del Rol <span class="text-rose-500">*</span>
                        </label>
                        <input
                            v-model="createForm.label"
                            type="text"
                            placeholder="Ej. Coordinador de Sede, Gestor de Pagos, Tutor..."
                            class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border rounded-xl text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                            :class="createErrors.label ? 'border-rose-400 dark:border-rose-500' : 'border-slate-200 dark:border-slate-700'"
                        />
                        <p v-if="createErrors.label" class="text-xs text-rose-500 mt-1 font-medium">{{ Array.isArray(createErrors.label) ? createErrors.label[0] : createErrors.label }}</p>
                    </div>

                    <!-- Descripción -->
                    <div>
                        <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                            Descripción / Función
                        </label>
                        <textarea
                            v-model="createForm.description"
                            rows="2"
                            placeholder="Breve detalle de las responsabilidades o alcance de este rol..."
                            class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                        ></textarea>
                    </div>

                    <!-- Clonar permisos iniciales de otro rol (Opcional) -->
                    <div>
                        <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                            Copiar permisos de otro rol (Opcional)
                        </label>
                        <select
                            v-model="createForm.cloneFromRoleId"
                            class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                        >
                            <option :value="null">-- Comenzar sin permisos iniciales --</option>
                            <option v-for="r in localRoles.filter(item => !item.is_reserved)" :key="r.id" :value="r.id">
                                {{ r.label }} ({{ (roleRightsMap[r.id] || []).length }} permisos)
                            </option>
                        </select>
                    </div>

                    <!-- Botones de Acción -->
                    <div class="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
                        <button
                            type="button"
                            @click="showCreateModal = false"
                            class="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-650 text-slate-700 dark:text-slate-200 font-semibold rounded-xl text-sm transition-colors cursor-pointer"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            :disabled="isCreatingRole"
                            class="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-sm transition-all shadow-md shadow-indigo-600/20 hover:shadow-indigo-600/30 flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                        >
                            <Icon v-if="isCreatingRole" icon="tabler:loader-2" class="w-4 h-4 animate-spin" />
                            <span>{{ isCreatingRole ? 'Creando Rol...' : 'Crear y Asignar Permisos' }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Modal Editar Datos del Rol -->
        <div v-if="showEditModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
            <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div class="p-6 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div class="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
                            <Icon icon="tabler:edit" class="w-6 h-6" />
                        </div>
                        <div>
                            <h3 class="text-lg font-bold text-slate-900 dark:text-white">Editar Información del Rol</h3>
                            <p class="text-xs text-slate-500 dark:text-slate-400">Modifique la etiqueta y descripción del rol #{{ editForm.id }}</p>
                        </div>
                    </div>
                    <button @click="showEditModal = false" class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer">
                        <Icon icon="tabler:x" class="w-5 h-5" />
                    </button>
                </div>

                <form @submit.prevent="submitUpdateRole" class="p-6 space-y-4">
                    <div>
                        <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                            Nombre del Rol <span class="text-rose-500">*</span>
                        </label>
                        <input
                            v-model="editForm.label"
                            type="text"
                            class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border rounded-xl text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                            :class="editErrors.label ? 'border-rose-400 dark:border-rose-500' : 'border-slate-200 dark:border-slate-700'"
                        />
                        <p v-if="editErrors.label" class="text-xs text-rose-500 mt-1 font-medium">{{ Array.isArray(editErrors.label) ? editErrors.label[0] : editErrors.label }}</p>
                    </div>

                    <div>
                        <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                            Descripción / Función
                        </label>
                        <textarea
                            v-model="editForm.description"
                            rows="2"
                            class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                        ></textarea>
                    </div>

                    <div class="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
                        <button
                            type="button"
                            @click="showEditModal = false"
                            class="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-650 text-slate-700 dark:text-slate-200 font-semibold rounded-xl text-sm transition-colors cursor-pointer"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            :disabled="isUpdatingRole"
                            class="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-sm transition-all shadow-md shadow-indigo-600/20 hover:shadow-indigo-600/30 flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                        >
                            <Icon v-if="isUpdatingRole" icon="tabler:loader-2" class="w-4 h-4 animate-spin" />
                            <span>{{ isUpdatingRole ? 'Guardando...' : 'Actualizar Rol' }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
    width: 5px;
}
.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(156, 163, 175, 0.3);
    border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(156, 163, 175, 0.5);
}
</style>
